import { toPretty } from "./prettyUrls";
import { applySeo, setSeoTitle } from "./seo";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// Clears the home-screen app-icon badge whenever the app is opened / brought back.
const toWorkers = (msg) => {
    try { navigator.serviceWorker?.controller?.postMessage(msg); } catch { /* noop */ }
    navigator.serviceWorker?.getRegistrations?.().then((rs) => rs.forEach((r) => r.active?.postMessage(msg))).catch(() => {});
};

// Inbox items (open tasks, unread messages, invites) stay on the icon until dealt with.
export function useInboxBadge(count) {
    useEffect(() => {
        const n = Math.max(0, Number(count) || 0);
        try { if (n > 0) navigator.setAppBadge?.(n); else navigator.clearAppBadge?.(); } catch { /* unsupported */ }
        toWorkers({ type: "rosterly-badge-base", base: n });
    }, [count]);
}

export function useClearAppBadge() {
    useEffect(() => {
        const clear = () => {
            if (document.visibilityState !== "visible") return;
            toWorkers({ type: "rosterly-clear-badge" });
        };
        clear();
        document.addEventListener("visibilitychange", clear);
        return () => document.removeEventListener("visibilitychange", clear);
    }, []);
}

const TITLES = [
    [/^\/nhl\/standings/, "NHL Standings"], [/^\/nhl\/schedule/, "NHL Schedule"], [/^\/nhl\/leaders/, "NHL Leaderboards"],
    [/^\/nhl\/lineups/, "NHL Lineups"], [/^\/nhl\/rosters/, "NHL Rosters"], [/^\/nhl\/chemistry/, "NHL Chemistry"],
    [/^\/nhl\/game\//, "NHL Game Centre"], [/^\/nhl\/team/, "NHL Team"], [/^\/nhl\/player/, "NHL Player"], [/^\/nhl/, "NHL"],
    [/^\/standings/, "PWHL Standings"], [/^\/schedule/, "PWHL Schedule"], [/^\/leaderboards/, "PWHL Leaderboards"],
    [/^\/game|^\/games\//, "PWHL Game Centre"], [/^\/teams?(\/|$)/, "PWHL Teams"], [/^\/lineups/, "PWHL Lineups"], [/^\/rosters/, "PWHL Rosters"],
    [/^\/players?\//, "Player"], [/^\/contracts/, "Contracts"], [/^\/draft/, "Draft"], [/^\/roadmap/, "Roadmap"],
    [/^\/stories/, "Stories"], [/^\/news/, "News"], [/^\/rankings/, "Rankings"], [/^\/compare/, "Player Compare"],
    [/^\/account|^\/me/, "My Account"], [/^\/admin/, "Admin"], [/^\/login/, "Sign In"], [/^\/setup/, "Setup"],
    [/^\/history|^\/changelog/, "History"], [/^\/search/, "Search"], [/^\/social/, "Social"], [/^\/archives/, "Archives"],
];
const nice = (path) => path.split("/").filter(Boolean).slice(0, 2).map((s) => decodeURIComponent(s).replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())).join(" ");

// League pages: the tab title always matches the public URL — /pwhl/leaderboards → "PWHL Leaderboards".
const SEG_LABEL = { game: "Game Centre", lineups: "Lineups", "staff-moves": "Staff Moves", player: "Player", team: "Team", coach: "Coach", leaders: "Leaderboards" };
export const routeName = (pathname) => {
    if (pathname === "/") return "";
    const pretty = toPretty(pathname);
    const [, lg, seg] = pretty.split("/");
    if (lg === "pwhl" || lg === "nhl" || lg === "ncaa" || lg === "nat") {
        const L = lg === "nat" ? "WWHC" : lg.toUpperCase();
        return seg ? `${L} ${SEG_LABEL[seg] || nice("/" + seg)}` : L;
    }
    const hit = TITLES.find(([re]) => re.test(pathname));
    return hit ? hit[1] : nice(pathname);
};

// "Rosterly - <Page>" on every route. Pages may refine it via setPageTitle().
export function useRouteTitle() {
    const { pathname } = useLocation();
    useEffect(() => {
        const name = routeName(pathname);
        document.title = name ? `Rosterly - ${name}` : "Rosterly";
        const seoName = applySeo(pathname);  // unique title + description + social tags per page
        if (seoName) document.title = `Rosterly - ${seoName}`;
        setSeoTitle(document.title);
    }, [pathname]);
}

export const setPageTitle = (name) => { document.title = name ? `Rosterly - ${name}` : "Rosterly"; setSeoTitle(document.title); };
// Pages with a specific subject (player, game, team, coach) name the tab after it.
export function usePageTitle(name) { useEffect(() => { if (name) setPageTitle(name); }, [name]); }
